import "./Gallery.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { GALLERY } from "../../../../data/gallery";
import Hourglass from "./Hourglass";
import { setTimeFrozen } from "../../../../utils/timeFreeze";

const SLIDE_MS = 6500; // how long each photo stays
const CURTAIN_DIM = 0.45; // how dark the photo gets as Releases slides over it
const SWIPE_DISTANCE = 50; // px a swipe must travel to change photo
const HOLD_MS = 280; // press this long (without moving) to freeze time

/*
 * Home page opener: a full-screen gallery that slowly crossfades between
 * photos.
 *
 * "Curtain": the gallery stays in place while the Releases section scrolls up
 * over it; meanwhile the photo dims and zooms in slightly. Photos don't zoom
 * on their own.
 *
 * For smooth scrolling (especially Safari), each scroll frame only changes
 * `transform` and `opacity` directly on the few elements involved: the
 * browser can do those on the graphics chip without redrawing the page.
 */
function Gallery() {
  const count = GALLERY.length;
  const sectionRef = useRef(null);
  const photosRef = useRef(null);
  const dimRef = useRef(null);
  const controlsRef = useRef(null);
  const hintRef = useRef(null);
  // Each visit starts on a random photo
  const [start] = useState(() => Math.floor(Math.random() * count));
  const [index, setIndex] = useState(start);
  const [previous, setPrevious] = useState(null);
  const [direction, setDirection] = useState(1); // 1 = went forward, -1 = back
  // Photos are only added to the page once needed (current + next), so the
  // first screen doesn't wait for all of them to download
  const [loaded, setLoaded] = useState(() => new Set([start, (start + 1) % count]));
  const [hidden, setHidden] = useState(false); // tab in the background
  const [covered, setCovered] = useState(false); // Releases fully over the gallery
  const [frozen, setFrozen] = useState(false); // photo held down: time stops

  const indexRef = useRef(start);
  const goTo = useCallback(
    (next, towards = 1) => {
      const current = indexRef.current;
      if (next === current) return;
      indexRef.current = next;
      setDirection(towards);
      setPrevious(current);
      setIndex(next);
      setLoaded((set) => new Set([...set, next, (next + 1) % count]));
    },
    [count]
  );

  // The photo on screen, for the Releases panel's blur stand-in
  // (Releases.css): wide or tall version, like the <picture> picks
  useEffect(() => {
    const photo = GALLERY[index];
    if (!photo) return;
    const tall = window.matchMedia?.("(orientation: portrait)").matches;
    document.documentElement.style.setProperty(
      "--gallery-photo",
      `url("${tall ? photo.tall : photo.wide}")`
    );
  }, [index]);

  // Next photo after SLIDE_MS, unless nobody can see the gallery or time is
  // frozen. After a pause it carries on where it stopped (like the hourglass),
  // instead of starting the photo's time over.
  const paused = hidden || covered || frozen || count < 2;
  const remainingRef = useRef(SLIDE_MS);
  useEffect(() => {
    remainingRef.current = SLIDE_MS; // new photo: full time
  }, [index]);
  useEffect(() => {
    if (paused) return undefined;
    const startedAt = performance.now();
    const timer = setTimeout(() => goTo((index + 1) % count), remainingRef.current);
    return () => {
      clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (performance.now() - startedAt));
    };
  }, [index, paused, count, goTo]);

  useEffect(() => {
    const onVisibility = () => setHidden(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Curtain progress: 0 at the top of the page, 1 once Releases covers the screen
  useEffect(() => {
    let frame = null;
    let last = -1;
    let wasCovered = false;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const update = () => {
      frame = null;
      const progress = Math.min(Math.max(window.scrollY / window.innerHeight, 0), 1);
      if (Math.abs(progress - last) < 0.001) return; // nothing visible changed
      last = progress;

      if (photosRef.current && !reduceMotion) {
        photosRef.current.style.transform = `translateZ(0) scale(${(1 + progress * 0.06).toFixed(4)})`;
      }
      if (dimRef.current) dimRef.current.style.opacity = (progress * CURTAIN_DIM).toFixed(3);
      const uiOpacity = Math.max(0, 1 - progress * 3);
      // Releases' shadow appears as it starts covering the gallery
      document
        .querySelector(".releases")
        ?.style.setProperty("--curtain", Math.min(1, progress * 4).toFixed(3));
      [controlsRef.current, hintRef.current].forEach((element) => {
        if (!element) return;
        element.style.opacity = uiOpacity.toFixed(3);
        element.style.visibility = uiOpacity === 0 ? "hidden" : "";
      });

      const isCovered = progress >= 1;
      if (isCovered !== wasCovered) {
        wasCovered = isCovered;
        setCovered(isCovered);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  // Changing photo by hand:
  // - swipe left/right (phones), or drag with the mouse
  // - the small ‹ / › beside the hourglass
  // - the ← / → keys while the gallery is on screen
  // Vertical swipes on phones still scroll the page (touch-action: pan-y).
  //
  // Pressing and holding the photo freezes time (EP "TIME"): the slideshow
  // stops, the photo loses its color and the clock in the top bar stops on
  // its seconds. Letting go brings it all back.
  const pointerRef = useRef(null);
  const holdTimerRef = useRef(null);
  const showNext = useCallback(() => goTo((indexRef.current + 1) % count), [goTo, count]);
  const showPrevious = useCallback(
    () => goTo((indexRef.current - 1 + count) % count, -1),
    [goTo, count]
  );

  const freeze = useCallback((on) => {
    setFrozen(on);
    setTimeFrozen(on);
  }, []);

  const endHold = () => {
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = null;
    if (pointerRef.current?.frozen) freeze(false);
    pointerRef.current = null;
  };

  // Gallery going away mid-hold: unfreeze the clock too
  useEffect(() => () => {
    clearTimeout(holdTimerRef.current);
    setTimeFrozen(false);
  }, []);

  const onPointerDown = (event) => {
    if (event.button !== 0 || event.target.closest("button")) return;
    const press = { x: event.clientX, y: event.clientY, frozen: false };
    pointerRef.current = press;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      press.frozen = true;
      freeze(true);
    }, HOLD_MS);
  };

  // Moving before the hold kicks in means it's a swipe, not a hold
  const onPointerMove = (event) => {
    const press = pointerRef.current;
    if (!press || press.frozen) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) {
      clearTimeout(holdTimerRef.current);
    }
  };

  const onPointerUp = (event) => {
    const press = pointerRef.current;
    if (!press) return;
    const dx = event.clientX - press.x;
    if (!press.frozen && count > 1 && Math.abs(dx) >= SWIPE_DISTANCE) {
      if (dx < 0) showNext();
      else showPrevious();
    }
    endHold();
  };

  // Arrow keys, while the gallery is visible and the Contact page isn't open
  useEffect(() => {
    if (count < 2) return undefined;
    const onKey = (event) => {
      if (covered || document.querySelector(".contact-page")) return;
      if (event.target.closest?.("input, textarea, [contenteditable='true']")) return;
      if (event.key === "ArrowRight") showNext();
      else if (event.key === "ArrowLeft") showPrevious();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, covered, showNext, showPrevious]);

  const scrollToReleases = () => {
    const releases = document.querySelector(".releases");
    if (!releases) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({
      top: releases.getBoundingClientRect().top + window.scrollY,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  return (
    <section
      className={`gallery ${paused ? "is-paused" : ""} ${frozen ? "is-frozen" : ""}`}
      ref={sectionRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={endHold}
      onContextMenu={(event) => event.preventDefault()}
      aria-label="Photos of thangcao"
      style={{ "--slide-ms": `${SLIDE_MS}ms` }}
    >
      <div className="gallery-photos" ref={photosRef}>
        {GALLERY.map((photo, i) =>
          loaded.has(i) ? (
            <picture
              key={photo.wide}
              className={`gallery-photo ${i === index ? "is-active" : ""} ${
                i === previous ? "is-previous" : ""
              }`}
            >
              {/* Phones held upright get the tall crop */}
              <source media="(orientation: portrait)" srcSet={photo.tall} />
              <img
                src={photo.wide}
                // Only the photo on screen is described to screen readers
                alt={i === index ? photo.alt : ""}
                style={{ "--position": photo.position }}
                decoding="async"
                draggable={false}
                fetchpriority={i === start ? "high" : "auto"}
              />
            </picture>
          ) : null
        )}
      </div>
      <div className="gallery-shade" aria-hidden="true" />
      {/* Ambient effects are hidden for now; to bring one back (they stop
          when time freezes), import it and add e.g.
          <LightTrail running={!hidden && !covered && !frozen} />
          Ready-made: LightTrail.js, Sand.js, Rain.js, Dust.js */}
      <div className="gallery-dim" ref={dimRef} aria-hidden="true" />

      {count > 1 ? (
        // Hourglass for the current photo's time, with small ‹ › on each side
        <div className="gallery-controls" ref={controlsRef}>
          <button
            className="gallery-arrow"
            onClick={showPrevious}
            aria-label="Previous photo"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          {/* Remounts for each photo and flips over (not on the very first
              photo): clockwise going forward, counter-clockwise going back */}
          <Hourglass
            key={index}
            duration={SLIDE_MS}
            paused={paused}
            flip={previous === null ? null : direction}
          />
          <button
            className="gallery-arrow"
            onClick={showNext}
            aria-label="Next photo"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      ) : null}

      <button className="gallery-hint" ref={hintRef} onClick={scrollToReleases}>
        <span>Releases</span>
        <span className="gallery-hint-line" aria-hidden="true" />
      </button>
    </section>
  );
}

export default Gallery;
